// Employee/components/View/TimeSlotCell.tsx
import React from "react";
import {Box, TableCell} from "@mui/material";
import {SxProps, Theme} from "@mui/material/styles";
import {Event, EventAddData} from "../../types";
import {addHours, differenceInMinutes, isEqual, isWithinInterval, parseISO,} from "date-fns";
import {EventCard} from "./EventCard";
import {keyframes} from "@mui/system";
import {Moment} from "moment";

// Animations
const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

interface TimeSlotCellProps {
    events: Event[];
    currentDay: string;
    time: string;
    onEdit: (event: Event | null) => void;
    onAdd: (eventData: EventAddData) => void;
    userType: "client" | "psychologist" | "observer";
    canEdit: boolean;
    onRemove: (event: Event) => void;
    onCreateAvailability: (start: Date, end: Date) => void;
    cellStyles: SxProps<Theme>;
    onSlotClick: (time: Date) => void;
    isFirstClicked: boolean | null;
    isAvailableForSelection: boolean;
    onConfirmSlot?: (slot: Moment) => void;
}

interface TimeInterval {
    start: Date;
    end: Date;
}

// Helper function to safely get backgroundColor from SxProps
const getBackgroundColorFromStyles = (styles: SxProps<Theme>): string | undefined => {
    if (typeof styles === 'object' && styles !== null && !Array.isArray(styles)) {
        const bgColor = (styles as Record<string, unknown>).backgroundColor;
        return typeof bgColor === 'string' ? bgColor : undefined;
    }
    return undefined;
};

export const TimeSlotCell: React.FC<TimeSlotCellProps> = ({
                                                              events,
                                                              currentDay,
                                                              time,
                                                              onEdit,
                                                              onAdd,
                                                              userType,
                                                              canEdit,
                                                              onRemove,
                                                              cellStyles,
                                                              onSlotClick,
                                                              isFirstClicked,
                                                              isAvailableForSelection,
                                                              onConfirmSlot,
                                                          }) => {
    const timeSlotStart = parseISO(`${currentDay}T${time.padStart(5, "0")}:00`);
    const timeSlotEnd = addHours(timeSlotStart, 1);

    // Get all events that overlap with this time slot
    const eventsInTimeSlot = events.filter((event) => {
        const eventStart = parseISO(event.start);
        const eventEnd = parseISO(event.end);
        return (
            (eventStart < timeSlotEnd && eventEnd > timeSlotStart) ||
            isEqual(eventStart, timeSlotStart) ||
            isEqual(eventEnd, timeSlotEnd)
        );
    }).sort((a, b) => parseISO(a.start).getTime() - parseISO(b.start).getTime());

    // Find all free intervals in this time slot
    const findFreeIntervals = (): TimeInterval[] => {
        if (eventsInTimeSlot.length === 0) {
            return [{start: timeSlotStart, end: timeSlotEnd}];
        }

        const freeIntervals: TimeInterval[] = [];
        let currentTime = timeSlotStart;

        eventsInTimeSlot.forEach((event) => {
            const eventStart = parseISO(event.start);
            const eventEnd = parseISO(event.end);

            // If there's a gap before this event
            if (currentTime < eventStart) {
                freeIntervals.push({
                    start: currentTime,
                    end: eventStart
                });
            }

            currentTime = eventEnd > currentTime ? eventEnd : currentTime;
        });

        // Check for remaining time after last event
        if (currentTime < timeSlotEnd) {
            freeIntervals.push({
                start: currentTime,
                end: timeSlotEnd
            });
        }

        // New logic to handle adding intervals forward
        if (eventsInTimeSlot.length > 0) {
            const lastEventEnd = parseISO(eventsInTimeSlot[eventsInTimeSlot.length - 1].end);
            if (lastEventEnd < timeSlotEnd) {
                freeIntervals.push({
                    start: lastEventEnd,
                    end: timeSlotEnd
                });
            }
        }

        return freeIntervals;
    };

    // Calculate background color based on available time
    const getBackgroundColor = () => {
        if (isFirstClicked) {
            return "rgba(0, 123, 255, 0.3)"; // Blue for first clicked slot
        }

        const freeIntervals = findFreeIntervals();
        if (freeIntervals.length === 0) return "inherit";

        const totalFreeMinutes = freeIntervals.reduce(
            (total, interval) => total + differenceInMinutes(interval.end, interval.start),
            0
        );
        const freeTimePercentage = totalFreeMinutes / 60;

        if (isAvailableForSelection && freeTimePercentage > 0) {
            return `rgba(0, 255, 0, ${freeTimePercentage * 0.1})`;
        }

        return getBackgroundColorFromStyles(cellStyles) || "inherit";
    };

    const handleCellClick = () => {
        if (userType !== "psychologist" || !canEdit) return;

        const freeIntervals = findFreeIntervals();
        if (freeIntervals.length === 0) return;

        // Select the first available interval
        onSlotClick(freeIntervals[0].start);
    };

    return (
        <TableCell
            sx={{
                ...cellStyles,
                backgroundColor: getBackgroundColor(),
                cursor: userType === "psychologist" && canEdit ? "pointer" : "default",
                transition: "background-color 0.3s ease-in-out",
                animation: (isFirstClicked || isAvailableForSelection) ? `${fadeIn} 0.3s ease-in-out` : "none",
            }}
            onClick={handleCellClick}
        >
            {eventsInTimeSlot.length > 0 && (
                <Box sx={{display: "flex", flexDirection: "column", gap: 0.5, height: "100%", position: "relative"}}>
                    {eventsInTimeSlot.map((event, index) => {
                        const eventStart = parseISO(event.start);
                        const eventEnd = parseISO(event.end);
                        const isFirstCell = isWithinInterval(eventStart, {start: timeSlotStart, end: timeSlotEnd});
                        const isLastCell = isWithinInterval(eventEnd, {start: timeSlotStart, end: timeSlotEnd});

                        // Check if the event is one hour or less
                        const isShortEvent = differenceInMinutes(eventEnd, eventStart) <= 60;

                        return (
                            <Box
                                key={event.id}
                                sx={{
                                    position: "relative",
                                    zIndex: index,
                                    cursor: "pointer",
                                    border: "none",
                                    boxShadow: "none",
                                    height: "100%",
                                    borderRadius: {
                                        topLeft: isFirstCell ? "4px" : 0,
                                        topRight: isFirstCell ? "4px" : 0,
                                        bottomLeft: isLastCell ? "4px" : 0,
                                        bottomRight: isLastCell ? "4px" : 0,
                                    },
                                }}
                            >
                                <EventCard
                                    event={event}
                                    onEdit={onEdit}
                                    onAdd={onAdd}
                                    userType={userType}
                                    timeSlotStart={timeSlotStart}
                                    timeSlotEnd={timeSlotEnd}
                                    removeGap={differenceInMinutes(eventEnd, eventStart) > 60}
                                    canEdit={canEdit}
                                    onRemove={onRemove}
                                    showStartTime={isFirstCell && !isShortEvent}
                                    showEndTime={isLastCell && !isShortEvent}
                                    eventStartInCell={eventStart < timeSlotStart ? timeSlotStart : eventStart}
                                    eventEndInCell={eventEnd > timeSlotEnd ? timeSlotEnd : eventEnd}
                                    isFirstCell={isFirstCell}
                                    isLastCell={isLastCell}
                                    onSlotSelected={onConfirmSlot}
                                    showTimeText={isFirstCell && isShortEvent}
                                />
                            </Box>
                        );
                    })}
                </Box>
            )}
        </TableCell>
    );
};
